import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  IconButton,
  TextField,
  Autocomplete,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';

import {
  getSubscriptionStatus,
  createCustomerPortalSession,
  createSetupSession,
  type SubscriptionStatus,
  type Subscription,
} from '@/services/subscriptionApi';

interface Props {
  onError?: (error: string) => void;
}

export default function SubscriptionCard({ onError }: Props) {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [billingPreference, setBillingPreference] = useState<'monthly' | 'yearly'>('monthly');
  const [pollingTimedOut, setPollingTimedOut] = useState(false);

  // Load subscription status
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const status = await getSubscriptionStatus();
        // eslint-disable-next-line no-console
        console.log('[SubscriptionCard] Loaded status:', status);
        setSubscriptionStatus(status);
        // Auto-select first subscription (excluding only canceled and incomplete_expired)
        if (status.subscriptions && status.subscriptions.length > 0) {
          // eslint-disable-next-line no-console
          console.log('[SubscriptionCard] Raw subscriptions:', status.subscriptions);
          const activeSubscriptions = status.subscriptions.filter((sub) => {
            // eslint-disable-next-line no-console
            console.log(
              `[SubscriptionCard] Checking subscription ${sub.subscription_id}: status=${sub.status}`,
            );
            return sub.status !== 'canceled' && sub.status !== 'incomplete_expired';
          });
          // eslint-disable-next-line no-console
          console.log('[SubscriptionCard] Active subscriptions after filter:', activeSubscriptions);
          if (activeSubscriptions.length > 0) {
            setSelectedSubscription(activeSubscriptions[0]);
            // eslint-disable-next-line no-console
            console.log('[SubscriptionCard] Selected subscription:', activeSubscriptions[0]);
          } else {
            console.warn(
              '[SubscriptionCard] No active subscriptions found (all are canceled or incomplete_expired)',
            );
          }
        } else {
          console.warn('[SubscriptionCard] No subscriptions in response');
        }
      } catch (error) {
        console.error('Failed to load subscription status:', error);
        onError?.('Failed to load subscription status');
      } finally {
        setLoading(false);
      }
    };

    loadStatus();
  }, [onError]);

  // Handle setup success redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const setupStatus = urlParams.get('setup');

    if (setupStatus === 'success') {
      // Show loading state while waiting for webhook
      setLoading(true);

      const refreshStatus = async () => {
        let attempts = 0;
        const maxAttempts = 8; // Increased from 5 to ~30 seconds total

        // Poll for payment method with exponential backoff
        const pollForPaymentMethod = async (): Promise<void> => {
          try {
            const status = await getSubscriptionStatus();

            // Check if payment method was added
            if (status.has_payment_methods) {
              setLoading(false);
              setSubscriptionStatus(status);
              if (status.subscriptions && status.subscriptions.length > 0) {
                const activeSubscriptions = status.subscriptions.filter(
                  (sub) => sub.status !== 'canceled',
                );
                if (activeSubscriptions.length > 0) {
                  setSelectedSubscription(activeSubscriptions[0]);
                }
              }
              return; // Success - stop polling
            }

            // If no payment method yet and we have attempts left, retry
            attempts++;
            if (attempts < maxAttempts) {
              const delay = Math.min(1000 * Math.pow(2, attempts), 5000); // Exponential backoff, max 5s
              await new Promise((resolve) => setTimeout(resolve, delay));
              return pollForPaymentMethod();
            } else {
              // Max attempts reached, show what we have
              setLoading(false);
              setPollingTimedOut(true);
              setSubscriptionStatus(status);
            }
          } catch (error) {
            console.error('Failed to refresh subscription status:', error);
            // On error, still try to show the state
            if (attempts < maxAttempts) {
              attempts++;
              const delay = Math.min(1000 * Math.pow(2, attempts), 5000);
              await new Promise((resolve) => setTimeout(resolve, delay));
              return pollForPaymentMethod();
            } else {
              setLoading(false);
            }
          }
        };

        await pollForPaymentMethod();
      };

      refreshStatus();
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  const handleGoToPortal = async () => {
    setPortalLoading(true);
    try {
      const response = await createCustomerPortalSession();
      window.location.href = response.portal_url;
    } catch (error) {
      console.error('Failed to create portal session:', error);
      onError?.('Failed to open customer portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleAddPaymentMethod = async () => {
    setSetupLoading(true);
    try {
      const response = await createSetupSession({
        billing_preference: billingPreference,
      });

      window.location.href = response.setup_url;
    } catch (error) {
      console.error('Failed to create setup session:', error);
      onError?.('Failed to add payment method');
    } finally {
      setSetupLoading(false);
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const calculateTotalAmount = (subscription: Subscription) => {
    if (subscription.line_items && subscription.line_items.length > 0) {
      return subscription.line_items.reduce(
        (total, item) => total + item.amount * item.quantity,
        0,
      );
    }
    return (subscription.amount || 0) * (subscription.quantity || 1);
  };

  const isScheduledForFuture = (subscription: Subscription) => {
    const now = Math.floor(Date.now() / 1000);

    // eslint-disable-next-line no-console
    console.log('[SubscriptionCard] isScheduledForFuture check:', {
      subscription_id: subscription.subscription_id,
      status: subscription.status,
      current_period_start: subscription.current_period_start,
      trial_end: subscription.trial_end,
      billing_cycle_anchor: subscription.billing_cycle_anchor,
      now,
      period_start_in_future: subscription.current_period_start
        ? subscription.current_period_start > now
        : false,
      trial_end_in_future: subscription.trial_end ? subscription.trial_end > now : false,
      billing_anchor_in_future: subscription.billing_cycle_anchor
        ? subscription.billing_cycle_anchor > now
        : false,
    });

    // Only treat as "scheduled" if the subscription hasn't actually started yet
    // Check if current_period_start is in the future
    if (subscription.current_period_start && subscription.current_period_start > now) {
      return true;
    }

    // Check if subscription is trialing with a future trial end
    if (
      subscription.status === 'trialing' &&
      subscription.trial_end &&
      subscription.trial_end > now
    ) {
      return true;
    }

    return false;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
      case 'unpaid':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatStatus = (subscription: Subscription) => {
    const status = subscription.status;

    // For trialing subscriptions, check if it's scheduled for future
    if (status === 'trialing' && isScheduledForFuture(subscription)) {
      return 'Scheduled';
    }

    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Active Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'unpaid':
        return 'Unpaid';
      case 'incomplete':
        return 'Incomplete';
      case 'incomplete_expired':
        return 'Expired';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
    }
  };

  if (loading) {
    const urlParams = new URLSearchParams(window.location.search);
    const setupStatus = urlParams.get('setup');
    const isProcessingPayment = setupStatus === 'success';

    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress />
          {isProcessingPayment && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Processing your payment method...
              <br />
              This may take a few moments.
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  if (!subscriptionStatus) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">Failed to load subscription information</Alert>
        </CardContent>
      </Card>
    );
  }

  const hasSubscriptions =
    subscriptionStatus?.subscriptions && subscriptionStatus.subscriptions.length > 0;
  // Filter out cancelled and incomplete_expired subscriptions for the dropdown
  // Keep incomplete, past_due, unpaid to show payment issues
  const activeSubscriptions =
    subscriptionStatus?.subscriptions?.filter(
      (sub) => sub.status !== 'canceled' && sub.status !== 'incomplete_expired',
    ) || [];
  const isMultiSubscription = activeSubscriptions.length > 1;

  // Check if there are canceled subscriptions (payment may have failed)
  const hasCanceledSubscriptions =
    subscriptionStatus?.subscriptions?.some((sub) => sub.status === 'canceled') || false;

  // eslint-disable-next-line no-console
  console.log('[SubscriptionCard] Render state:', {
    hasSubscriptions,
    activeSubscriptionsLength: activeSubscriptions.length,
    hasPaymentMethods: subscriptionStatus?.has_payment_methods,
    hasCanceledSubscriptions,
  });

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Stack
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" gutterBottom>
              SimpleSUB Service Fee
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Manage your SimpleSUB service fee payment methods, billing details, and invoice
              history.
            </Typography>

            {pollingTimedOut && !subscriptionStatus?.has_payment_methods && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your payment method is being processed. If it doesn't appear shortly, please{' '}
                <button
                  onClick={() => {
                    window.location.reload();
                  }}
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  refresh the page
                </button>
                .
              </Alert>
            )}

            {activeSubscriptions.length > 0 ? (
              <Stack spacing={3}>
                {isMultiSubscription && (
                  <Box>
                    <Typography fontSize="16px" fontWeight={400} marginBottom={1}>
                      Select Fee
                    </Typography>
                    <Autocomplete
                      options={activeSubscriptions}
                      getOptionLabel={(option) => option.display_name}
                      value={selectedSubscription}
                      onChange={(_, newValue) => {
                        setSelectedSubscription(newValue);
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.subscription_id === value.subscription_id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search or select property..."
                          variant="outlined"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '25px',
                            },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body1">{option.display_name}</Typography>
                            {option.description && (
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      sx={{ width: '100%' }}
                    />
                  </Box>
                )}

                {selectedSubscription &&
                  (() => {
                    // eslint-disable-next-line no-console
                    console.log('[SubscriptionCard] Rendering subscription:', selectedSubscription);
                    return (
                      <Stack spacing={2}>
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="body1">Status:</Typography>
                          <Chip
                            label={formatStatus(selectedSubscription)}
                            color={getStatusColor(selectedSubscription.status)}
                            size="small"
                          />
                        </Box>

                        {subscriptionStatus?.customer_email && (
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body1" sx={{ flexShrink: 0 }}>
                              Recipient:
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                wordBreak: 'break-all',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {subscriptionStatus.customer_email}
                            </Typography>
                          </Box>
                        )}

                        {selectedSubscription.amount || selectedSubscription.line_items?.length ? (
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography variant="body1">Amount:</Typography>
                            {selectedSubscription.line_items &&
                            selectedSubscription.line_items.length > 1 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    {selectedSubscription.line_items.map((item, index) => (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          minWidth: 200,
                                          mb:
                                            index < selectedSubscription.line_items!.length - 1
                                              ? 0.5
                                              : 0,
                                        }}
                                      >
                                        <Typography variant="caption" sx={{ mr: 2 }}>
                                          {item.quantity} ×{' '}
                                          {formatAmount(item.amount, item.currency || 'usd')}
                                          {item.description && ` (${item.description})`}:
                                        </Typography>
                                        <Typography variant="caption">
                                          {formatAmount(
                                            item.amount * item.quantity,
                                            item.currency || 'usd',
                                          )}
                                        </Typography>
                                      </Box>
                                    ))}
                                    <Box
                                      sx={{
                                        borderTop: 1,
                                        borderColor: 'divider',
                                        mt: 0.5,
                                        pt: 0.5,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 'bold', mr: 2 }}
                                      >
                                        Total/{selectedSubscription.interval}:
                                      </Typography>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        {formatAmount(
                                          calculateTotalAmount(selectedSubscription),
                                          selectedSubscription.currency || 'usd',
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Typography
                                  component="span"
                                  variant="body1"
                                  fontWeight="bold"
                                  sx={{
                                    borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                    cursor: 'help',
                                    '&:hover': {
                                      borderBottomColor: 'primary.main',
                                    },
                                  }}
                                >
                                  {formatAmount(
                                    calculateTotalAmount(selectedSubscription),
                                    selectedSubscription.currency || 'usd',
                                  )}
                                  /{selectedSubscription.interval}
                                </Typography>
                              </Tooltip>
                            ) : selectedSubscription.line_items &&
                              selectedSubscription.line_items.length === 1 &&
                              selectedSubscription.line_items[0].quantity > 1 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minWidth: 200,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ mr: 2 }}>
                                        {selectedSubscription.line_items[0].quantity} devices ×{' '}
                                        {formatAmount(
                                          selectedSubscription.line_items[0].amount,
                                          selectedSubscription.line_items[0].currency || 'usd',
                                        )}
                                        :
                                      </Typography>
                                      <Typography variant="caption">
                                        {formatAmount(
                                          calculateTotalAmount(selectedSubscription),
                                          selectedSubscription.currency || 'usd',
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Typography
                                  component="span"
                                  variant="body1"
                                  fontWeight="bold"
                                  sx={{
                                    borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                    cursor: 'help',
                                    '&:hover': {
                                      borderBottomColor: 'primary.main',
                                    },
                                  }}
                                >
                                  {formatAmount(
                                    calculateTotalAmount(selectedSubscription),
                                    selectedSubscription.currency || 'usd',
                                  )}
                                  /{selectedSubscription.interval}
                                </Typography>
                              </Tooltip>
                            ) : !selectedSubscription.line_items &&
                              selectedSubscription.quantity &&
                              selectedSubscription.quantity > 1 ? (
                              <Tooltip
                                title={
                                  <Box>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        minWidth: 200,
                                      }}
                                    >
                                      <Typography variant="caption" sx={{ mr: 2 }}>
                                        {selectedSubscription.quantity} devices ×{' '}
                                        {formatAmount(
                                          selectedSubscription.amount || 0,
                                          selectedSubscription.currency || 'usd',
                                        )}
                                        :
                                      </Typography>
                                      <Typography variant="caption">
                                        {formatAmount(
                                          calculateTotalAmount(selectedSubscription),
                                          selectedSubscription.currency || 'usd',
                                        )}
                                      </Typography>
                                    </Box>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Typography
                                  component="span"
                                  variant="body1"
                                  fontWeight="bold"
                                  sx={{
                                    borderBottom: '1px dotted rgba(0, 0, 0, 0.3)',
                                    cursor: 'help',
                                    '&:hover': {
                                      borderBottomColor: 'primary.main',
                                    },
                                  }}
                                >
                                  {formatAmount(
                                    calculateTotalAmount(selectedSubscription),
                                    selectedSubscription.currency || 'usd',
                                  )}
                                  /{selectedSubscription.interval}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="body1" fontWeight="bold">
                                {formatAmount(
                                  calculateTotalAmount(selectedSubscription),
                                  selectedSubscription.currency || 'usd',
                                )}
                                /{selectedSubscription.interval}
                              </Typography>
                            )}
                          </Box>
                        ) : null}

                        {selectedSubscription.status !== 'canceled' &&
                          (() => {
                            // For scheduled subscriptions that haven't started, show start date
                            if (isScheduledForFuture(selectedSubscription)) {
                              const startDate =
                                selectedSubscription.trial_end ||
                                selectedSubscription.current_period_start;
                              if (startDate) {
                                return (
                                  <Box display="flex" alignItems="center" gap={2}>
                                    <Typography variant="body1">Starts on:</Typography>
                                    <Typography variant="body1">{formatDate(startDate)}</Typography>
                                  </Box>
                                );
                              }
                            }
                            // For active/trialing subscriptions, show next billing date
                            if (selectedSubscription.current_period_end) {
                              return (
                                <Box display="flex" alignItems="center" gap={2}>
                                  <Typography variant="body1">Next billing:</Typography>
                                  <Typography variant="body1">
                                    {formatDate(selectedSubscription.current_period_end)}
                                  </Typography>
                                </Box>
                              );
                            }
                            return null;
                          })()}

                        {isScheduledForFuture(selectedSubscription) && (
                          <Alert severity="info">
                            Your subscription is scheduled to start on{' '}
                            {formatDate(
                              selectedSubscription.trial_end ||
                                selectedSubscription.current_period_start!,
                            )}
                            . You will be charged when the subscription starts.
                          </Alert>
                        )}

                        {selectedSubscription.status === 'incomplete' && (
                          <Alert severity="error">
                            Initial payment failed. Your subscription is not yet active. Please
                            update your payment method or contact support.
                          </Alert>
                        )}

                        {(selectedSubscription.status === 'past_due' ||
                          selectedSubscription.status === 'unpaid') && (
                          <Alert severity="error">
                            Payment failed. Please update your payment method or contact support to
                            restore service.
                          </Alert>
                        )}

                        {selectedSubscription.status === 'canceled' && (
                          <Alert severity="warning">
                            This subscription has been canceled. Contact customer support to
                            reactivate.
                          </Alert>
                        )}
                      </Stack>
                    );
                  })()}
              </Stack>
            ) : subscriptionStatus?.has_payment_methods ? (
              <Stack spacing={2}>
                {hasCanceledSubscriptions ? (
                  <Alert severity="error">
                    Your subscription was canceled. Please contact support to set up a new
                    subscription.
                  </Alert>
                ) : (
                  <Alert severity="success">
                    Payment method added successfully! <br /> Our team will set up your subscription
                    based on your device(s).
                  </Alert>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Alert
                  severity="warning"
                  sx={{
                    '& .MuiAlert-icon': {
                      alignSelf: 'center',
                    },
                  }}
                >
                  Add a payment method to get started. Our team will set up your subscription based
                  on your device(s).
                </Alert>

                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    Billing Preference
                    <Tooltip
                      placement="right"
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Device Pricing Breakdown
                          </Typography>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr 1fr',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              Device
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              Monthly
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              Yearly
                            </Typography>
                          </Box>
                          {[
                            { sku: 'i1', monthly: '$6', yearly: '$60' },
                            { sku: 'x1', monthly: '$6', yearly: '$60' },
                            { sku: 'x2', monthly: '$10', yearly: '$96' },
                            { sku: 'Z1', monthly: '$5', yearly: '$60' },
                          ].map((device) => (
                            <Box
                              key={device.sku}
                              sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}
                            >
                              <Typography variant="caption">{device.sku}</Typography>
                              <Typography variant="caption">{device.monthly}</Typography>
                              <Typography variant="caption">{device.yearly}</Typography>
                            </Box>
                          ))}
                          <Typography
                            variant="caption"
                            sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}
                          >
                            Your total cost depends on the devices installed at your property.
                          </Typography>
                        </Box>
                      }
                    >
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon
                          sx={{
                            fontSize: '17px',
                            color: '#555',
                            ml: 0.5,
                            mb: 0.25,
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </FormLabel>
                  <RadioGroup
                    value={billingPreference}
                    onChange={(e) => setBillingPreference(e.target.value as 'monthly' | 'yearly')}
                  >
                    <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                    <FormControlLabel
                      value="yearly"
                      control={<Radio />}
                      label="Yearly (discount)"
                    />
                  </RadioGroup>
                </FormControl>
              </Stack>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {hasSubscriptions ? (
              selectedSubscription?.status === 'canceled' ? (
                <LoadingButton
                  variant="contained"
                  onClick={handleAddPaymentMethod}
                  loading={setupLoading}
                  disabled={setupLoading}
                >
                  Add New Payment Method
                </LoadingButton>
              ) : (
                <LoadingButton
                  variant="contained"
                  onClick={handleGoToPortal}
                  loading={portalLoading}
                  disabled={portalLoading}
                >
                  Manage Billing
                </LoadingButton>
              )
            ) : subscriptionStatus?.has_payment_methods ? (
              <LoadingButton
                variant="contained"
                onClick={handleGoToPortal}
                loading={portalLoading}
                disabled={portalLoading}
              >
                Manage Payment Methods
              </LoadingButton>
            ) : (
              <LoadingButton
                variant="contained"
                onClick={handleAddPaymentMethod}
                loading={setupLoading}
                disabled={setupLoading}
              >
                Add Payment Method
              </LoadingButton>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
